import React, { useState, useMemo } from 'react';
import { Box, Card, CardContent, Chip, IconButton, Menu, MenuItem, Stack, ToggleButton, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { WarbandMemberDTO } from '../../types';
import { PopoverItem } from '../../hooks/useItemPopover';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { useAuthStore } from '../../stores/authStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { editButtonOffsetSx, treasureToggleSx } from '../../App';
import { hashStringToSeed, generateBloodSpatterSVG, createSubtleIceSurface } from '../../theme/surfaces';
import { EditNameDialog } from './EditNameDialog';
import { MemberImage } from './MemberImage';
import { MemberStatsAndHealth } from './MemberStatsAndHealth';
import { MemberWeapons } from './MemberWeapons';
import { MemberArmour } from './MemberArmour';
import { MemberMagicItems } from './MemberMagicItems';
import { MemberNotes } from './MemberNotes';
import { MemberEffects } from './MemberEffects';
import { MemberPermanentInjuries } from './MemberPermanentInjuries';
import { MemberTraits } from './MemberTraits';

export interface WarbandMemberCardProps {
  member: WarbandMemberDTO;
  title?: string;
  isDefeated?: boolean;
  titleActionContent?: React.ReactNode;
  titleMenuItems?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }[];
  bottomRightActionContent?: React.ReactNode;
  showArmourWhenEmpty?: boolean;
  onItemClick: (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  editMode?: boolean;
  unusedLevelUps?: number;
  onApplyLevelUp?: (stat: string) => Promise<void>;
  levelUpBonuses?: { fight: number; shoot: number; will: number; health: number };
}

export const WarbandMemberCard = React.memo(function WarbandMemberCard({
  member,
  title = member.name,
  isDefeated = false,
  titleActionContent,
  titleMenuItems = [],
  bottomRightActionContent,
  showArmourWhenEmpty,
  onItemClick,
  editMode = false,
  unusedLevelUps,
  onApplyLevelUp,
  levelUpBonuses,
}: WarbandMemberCardProps) {
  const { warbandId, id: memberId, creatureName } = member;

  const memberRole: 'wizard' | 'apprentice' | 'soldier' =
    member.creatureId?.toLowerCase() === 'wizard' ? 'wizard'
      : member.creatureId?.toLowerCase() === 'apprentice' ? 'apprentice'
        : 'soldier';

  const updateMemberBuffs = useDirtyTrackingStore((s) => s.updateMemberBuffs);
  const updateWarbandBuffs = useDirtyTrackingStore((s) => s.updateWarbandBuffs);
  const updateMemberNotes = useDirtyTrackingStore((s) => s.updateMemberNotes);
  const updateMemberWeapons = useDirtyTrackingStore((s) => s.updateMemberWeapons);
  const updateMemberTreasureItems = useDirtyTrackingStore((s) => s.updateMemberTreasureItems);
  const updateMemberName = useDirtyTrackingStore((s) => s.updateMemberName);
  const updateMemberHealth = useDirtyTrackingStore((s) => s.updateMemberHealth);
  const toggleMemberCarryingTreasure = useDirtyTrackingStore((s) => s.toggleMemberCarryingTreasure);
  const updateMemberPermanentInjuries = useDirtyTrackingStore((s) => s.updateMemberPermanentInjuries);
  const warbandBuffs = useWarbandStore((s) => s.warbandBuffs);
  const accessToken = useAuthStore((s) => s.accessToken);
  const getCreatureById = useReferenceDataStore((s) => s.getCreatureById);
  const allBuffs = useReferenceDataStore((s) => s.buffs);

  const creature = getCreatureById(member.creatureId);
  const canCarryTreasure = (creature?.canCarryTreasure ?? 0) !== 0;
  const traits = creature?.traits ?? [];

  // Weapon name override: treasure items that are based on a weapon use the treasure item's name/description.
  const treasureItems = member.treasureItems ?? [];
  const weapons = (member.weapons ?? []).map((w) => {
    const idx = treasureItems.findIndex((t) => t.baseItemId === w.id);
    return idx !== -1
      ? { ...w, name: treasureItems[idx].name, description: treasureItems[idx].description }
      : { ...w, description: '' };
  });
  const armours = member.armours ?? [];

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);

  const bloodSpatterUrl = useMemo(() => generateBloodSpatterSVG(hashStringToSeed(memberId)), [memberId]);
  const frostedGlassCardSx = useMemo(() => createSubtleIceSurface(hashStringToSeed(memberId), {}), [memberId]);

  return (
    <Card sx={{ ...frostedGlassCardSx, position: 'relative' }} elevation={3}>
      {isDefeated && (
        <Box
          aria-hidden
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
          sx={{
            borderRadius: 'inherit',
            pointerEvents: 'none',
            backgroundImage: bloodSpatterUrl,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <CardContent style={{ position: 'relative', zIndex: 3, height: '100%' }}>
        <Stack direction="column" justifyContent="space-between"  height="100%">
          <Box  height="100%">
            {titleMenuItems.length > 0 && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  aria-label="member actions"
                  aria-controls={isMenuOpen ? 'warband-member-actions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen ? 'true' : undefined}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  id="warband-member-actions-menu"
                  anchorEl={menuAnchorEl}
                  open={isMenuOpen}
                  onClose={() => setMenuAnchorEl(null)}
                >
                  {titleMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      disabled={item.disabled}
                      onClick={() => {
                        setMenuAnchorEl(null);
                        item.onClick();
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'flex-start',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <MemberImage
                warbandId={warbandId}
                memberId={memberId}
                editMode={editMode}
                isTemporary={member.isTemporary}
                accessToken={accessToken}
              />
              {/* Main content, fills remaining space */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {/* Title / name row */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {title}
                          </Typography>
                          {!editMode && member.leftTable && <Chip label="Left Table" size="small" color="warning" />}
                          {member.isTemporary && <Chip label="Temporary" size="small" color="info" />}
                          {editMode && (
                            <IconButton sx={editButtonOffsetSx} size="small" onClick={() => setIsEditNameOpen(true)}>
                              <MoreHorizIcon />
                            </IconButton>
                          )}
                        </Stack>
                        {creatureName && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            {creatureName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {titleActionContent}
                      </Box>
                    </Box>
                    <MemberStatsAndHealth
                      member={member}
                      editMode={editMode}
                      levelUpBonuses={levelUpBonuses}
                      unusedLevelUps={unusedLevelUps}
                      onApplyLevelUp={onApplyLevelUp}
                      onUpdateHealth={(health) => updateMemberHealth(memberId, memberRole, health)}
                      warbandBuffs={warbandBuffs}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'flex-start',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <MemberWeapons
                weapons={weapons}
                editMode={editMode}
                memberRole={memberRole}
                memberId={memberId}
                memberName={member.name}
                onItemClick={onItemClick}
                onSaveWeapons={(weaponIds) => updateMemberWeapons(memberId, memberRole as 'wizard' | 'apprentice', weaponIds)}
              />
              {(showArmourWhenEmpty || armours.length > 0) && (
                <MemberArmour armours={armours} onItemClick={onItemClick} />
              )}
              <MemberMagicItems
                member={member}
                memberRole={memberRole}
                editMode={editMode}
                isDefeated={isDefeated}
                onItemClick={onItemClick}
                allBuffs={allBuffs}
                warbandBuffs={warbandBuffs}
                onUpdateMemberBuffs={(buffIds) => updateMemberBuffs(memberId, memberRole, buffIds)}
                onUpdateWarbandBuffs={updateWarbandBuffs}
                onUpdateTreasureItems={(itemIds) => updateMemberTreasureItems(memberId, memberRole, itemIds)}
              />
              <MemberNotes
                notes={member.notes}
                editMode={editMode}
                onSaveNotes={(newNotes) => updateMemberNotes(memberId, memberRole, newNotes)}
              />
              <MemberPermanentInjuries
                injuries={member.permanentInjuries ?? []}
                editMode={editMode}
                memberRole={memberRole}
                member={member}
                onUpdateInjuries={(injuryIds) => updateMemberPermanentInjuries(memberId, memberRole as 'wizard' | 'apprentice', injuryIds)}
              />
              <MemberTraits traits={traits} onItemClick={onItemClick} />
            </Box>
            <MemberEffects
              member={member}
              memberRole={memberRole}
              editMode={editMode}
              isDefeated={isDefeated}
              onUpdateMemberBuffs={(buffIds) => updateMemberBuffs(memberId, memberRole, buffIds)}
            />
          </Box>
          {(!editMode || bottomRightActionContent) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {!editMode && canCarryTreasure && (
                <ToggleButton
                  value="carrying-treasure"
                  selected={member.carryingTreasure}
                  onChange={() => toggleMemberCarryingTreasure(memberId, memberRole)}
                  size="small"
                  aria-label="carrying treasure"
                  sx={treasureToggleSx}
                >
                  Treasure
                </ToggleButton>
              )}
              {!editMode && !canCarryTreasure && (
                <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
                  Cannot carry treasure
                </Typography>
              )}
              {bottomRightActionContent}
            </Box>
          )}
        </Stack>
      </CardContent>

      <EditNameDialog
        open={isEditNameOpen}
        label="Name"
        initialValue={member.name}
        onClose={() => setIsEditNameOpen(false)}
        onSave={(newName) => {
          updateMemberName(memberId, memberRole, newName);
          setIsEditNameOpen(false);
        }}
      />
    </Card>
  );
});